import { getUserQnAList } from '@ninjacart/nc-nk-advisory';
import { IActionDataModel, IAdaptiveActionCard, IQnAListPayload, IQuestionAnswer } from '@ninjacart/nc-nk-core';
import { create } from 'zustand';
import { getAllVideoList } from '../api/kisan/getAllVideoList';
import { getAllProfileActivity } from '../api/kisan/getAllActivity';
interface UserEngagementState {
  userQnaFeed: IAdaptiveActionCard<IActionDataModel<IQuestionAnswer>>[];
  homeFeedsCard: IAdaptiveActionCard[];
  profileActivity: {
    comments: any[];
    likes: any[];
  } | null;
  isQnaLoaded: boolean;
  isVideoLoaded: boolean;
  isProfileActivityLoaded: boolean;
  isQnaLoading: boolean;
  isVideoLoading: boolean;
  isProfileActivityLoading: boolean;
  qnaError: string | null;
  videoError: string | null;
  profileActivityError: string | null;
  fetchQuestionData: (payload: IQnAListPayload, refreshing: boolean) => Promise<void>;
  fetchAllVideosData: (
    payload: { userId: string; offset: number; count: number },
    refreshing: boolean
  ) => Promise<void>;
  fetchProfileActivity: (payload: object, refreshing: boolean) => Promise<void>;
  resetQnaFeed: () => void;
  resetHomeFeedsCard: () => void;
  resetProfileActivity: () => void;
}

const useUserEngagementStore = create<UserEngagementState>((set) => ({
  userQnaFeed: [],
  homeFeedsCard: [],
  profileActivity: null,
  isQnaLoaded: false,
  isVideoLoaded: false,
  isProfileActivityLoaded: false,
  isQnaLoading: false,
  isVideoLoading: false,
  isProfileActivityLoading: false,
  qnaError: null,
  videoError: null,
  profileActivityError: null,

  fetchQuestionData: async (payload, refreshing) => {
    set({ isQnaLoading: true, qnaError: null });
    try {
      const qnaList = await getUserQnAList(payload);
      set((state) => ({
        userQnaFeed: refreshing ? qnaList : [...state.userQnaFeed, ...qnaList],
        isQnaLoaded: true,
        isQnaLoading: false,
      }));
    } catch (error) {
      console.error('Error fetching QnA List:', error);
      set({ qnaError: 'Failed to load QnA List', isQnaLoading: false });
    }
  },

  fetchAllVideosData: async (payload, refreshing) => {
    set({ isVideoLoading: true, videoError: null });
    try {
      const videoList = await getAllVideoList(payload);
      set((state) => ({
        homeFeedsCard: refreshing
          ? videoList.data || []
          : [...state.homeFeedsCard, ...(videoList.data || [])],
        isVideoLoaded: true,
        isVideoLoading: false,
      }));
    } catch (error) {
      console.error('Error fetching Video List:', error);
      set({ videoError: 'Failed to load Video List', isVideoLoading: false });
    }
  },

  fetchProfileActivity: async (payload, refreshing) => {
    set({ isProfileActivityLoading: true, profileActivityError: null });
    try {
      const activityData = await getAllProfileActivity(payload);

      set((state) => {
        const existingCommentIds = new Set(state.profileActivity?.comments.map((item) => item.id));
        const existingLikeIds = new Set(state.profileActivity?.likes.map((item) => item.id));

        const newComments = (activityData.comments || []).filter(
          (item) => !existingCommentIds.has(item.id)
        );
        const newLikes = (activityData.likes || []).filter((item) => !existingLikeIds.has(item.id));

        const updatedComments = refreshing
          ? newComments
          : [...(state.profileActivity?.comments || []), ...newComments];
        const updatedLikes = refreshing
          ? newLikes
          : [...(state.profileActivity?.likes || []), ...newLikes];

        return {
          profileActivity: {
            comments: updatedComments,
            likes: updatedLikes,
          },
          isProfileActivityLoaded: true,
          isProfileActivityLoading: false,
        };
      });
    } catch (error) {
      console.error('Error fetching Profile Activity:', error);
      set({
        profileActivityError: 'Failed to load Profile Activity',
        isProfileActivityLoading: false,
      });
    }
  },

  resetQnaFeed: () =>
    set({
      userQnaFeed: [],
      isQnaLoaded: false,
      isQnaLoading: false,
      qnaError: null,
    }),

  resetHomeFeedsCard: () =>
    set({
      homeFeedsCard: [],
      isVideoLoaded: false,
      isVideoLoading: false,
      videoError: null,
    }),

  resetProfileActivity: () =>
    set({
      profileActivity: null,
      isProfileActivityLoaded: false,
      isProfileActivityLoading: false,
      profileActivityError: null,
    }),
}));

export default useUserEngagementStore;
